import { useState, useCallback, useEffect, useRef } from 'react'
import { useDropzone } from 'react-dropzone'
import { Button } from './ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog'
import { Badge } from './ui/badge'
import { mdiAlert, mdiCheck, mdiClose, mdiUpload } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { usePropertyImport } from '../hooks/usePropertyImport'
import { EmptyState } from './ui/empty-state'
import { useTranslation } from 'react-i18next'

interface ImportItem {
  file: File
  property: any
  status: 'pending' | 'success' | 'error'
  error?: string
  action: 'create' | 'update' | 'skip'
}

export function PropertyImport() {
  const { t } = useTranslation('properties')
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [importQueue, setImportQueue] = useState<ImportItem[]>([])
  const [isImporting, setIsImporting] = useState(false)
  const [progress, setProgress] = useState(0)
  const [isDraggingOverPage, setIsDraggingOverPage] = useState(false)
  const dragCounterRef = useRef(0)
  const { importProperty } = usePropertyImport()

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setIsDraggingOverPage(false)
    dragCounterRef.current = 0

    const newItems: ImportItem[] = []

    for (const file of acceptedFiles) {
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        newItems.push({
          file,
          property: null,
          status: 'error',
          error: 'File size exceeds 5MB limit',
          action: 'skip'
        })
        continue
      }

      try {
        const text = await file.text()
        const propertyData = JSON.parse(text)

        // Basic validation
        if (!propertyData.code || !propertyData.data_type) {
          newItems.push({
            file,
            property: null,
            status: 'error',
            error: 'Missing required fields: code or data_type',
            action: 'skip'
          })
          continue
        }

        // Validate data_type
        const validDataTypes = ['text', 'number', 'boolean', 'date', 'enum', 'json']
        if (!validDataTypes.includes(propertyData.data_type)) {
          newItems.push({
            file,
            property: null,
            status: 'error',
            error: `Invalid data_type. Must be one of: ${validDataTypes.join(', ')}`,
            action: 'skip'
          })
          continue
        }

        newItems.push({
          file,
          property: propertyData,
          status: 'pending',
          action: 'create'
        })
      } catch {
        newItems.push({
          file,
          property: null,
          status: 'error',
          error: 'Invalid JSON format',
          action: 'skip'
        })
      }
    }

    setImportQueue(prev => [...prev, ...newItems])
    setIsDialogOpen(true)
  }, [])

  // Track page-level drag events to show overlay
  useEffect(() => {
    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault()
      dragCounterRef.current++
      if (e.dataTransfer?.types.includes('Files')) {
        setIsDraggingOverPage(true)
      }
    }

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault()
      dragCounterRef.current--
      if (dragCounterRef.current === 0) {
        setIsDraggingOverPage(false)
      }
    }

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault()
    }

    const handleDrop = () => {
      dragCounterRef.current = 0
      setIsDraggingOverPage(false)
    }

    document.addEventListener('dragenter', handleDragEnter)
    document.addEventListener('dragleave', handleDragLeave)
    document.addEventListener('dragover', handleDragOver)
    document.addEventListener('drop', handleDrop)

    return () => {
      document.removeEventListener('dragenter', handleDragEnter)
      document.removeEventListener('dragleave', handleDragLeave)
      document.removeEventListener('dragover', handleDragOver)
      document.removeEventListener('drop', handleDrop)
    }
  }, [])

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept: {
      'application/json': ['.json']
    },
    multiple: true,
    noClick: true,
    noKeyboard: true,
  })

  const handleImport = async () => {
    setIsImporting(true)
    setProgress(0)

    const itemsToImport = importQueue.filter(item => item.status === 'pending' && item.action !== 'skip')

    for (let i = 0; i < itemsToImport.length; i++) {
      const item = itemsToImport[i]

      try {
        console.log('Starting import for:', item.file.name)
        await importProperty(item.property, item.action as 'create' | 'update')
        console.log('Successfully imported:', item.file.name)

        setImportQueue(prev => prev.map(queueItem =>
          queueItem.file.name === item.file.name
            ? { ...queueItem, status: 'success' as const }
            : queueItem
        ))
      } catch (error) {
        console.error('Import failed for:', item.file.name, error)
        const errorMessage = (error as any)?.title || (error as any)?.message || (error as any)?.detail || JSON.stringify(error)

        setImportQueue(prev => prev.map(queueItem =>
          queueItem.file.name === item.file.name
            ? {
              ...queueItem,
              status: 'error' as const,
              error: errorMessage
            }
            : queueItem
        ))
      }

      setProgress(((i + 1) / itemsToImport.length) * 100)
    }

    setIsImporting(false)
  }

  const handleRemoveItem = (fileName: string) => {
    setImportQueue(prev => prev.filter(item => item.file.name !== fileName))
  }

  const handleClearAll = () => {
    setImportQueue([])
    setIsDialogOpen(false)
  }

  return (
    <>
      <input {...getInputProps()} />
      <Button variant="outline" onClick={open} data-testid="property-import-btn">
        <AppIcon path={mdiUpload} size="sm" className="mr-2" />
        Import
      </Button>

      {isDraggingOverPage && (
        <div
          {...getRootProps()}
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm"
        >
          <div className="border-2 border-dashed border-primary rounded-xl p-12 text-center bg-card shadow-lg">
            <AppIcon path={mdiUpload} size="2xl" className="mx-auto mb-4 text-primary" />
            <h3 className="text-lg font-medium mb-1">Drop property files here</h3>
            <p className="text-sm text-muted-foreground">
              Import property definitions from JSON files
            </p>
          </div>
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Properties</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {importQueue.map((item, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 border rounded-lg">
                <div className="flex-shrink-0">
                  {item.status === 'pending' && <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />}
                  {item.status === 'success' && <AppIcon path={mdiCheck} size="md" className="text-green-500" />}
                  {item.status === 'error' && <AppIcon path={mdiAlert} size="md" className="text-red-500" />}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{item.file.name}</p>
                  {item.property && (
                    <p className="text-xs text-muted-foreground">
                      {item.property.name?.en || item.property.name?.fr || item.property.code}
                    </p>
                  )}
                  {item.error && (
                    <p className="text-xs text-red-500 mt-1">{item.error}</p>
                  )}
                </div>

                {item.status === 'pending' && (
                  <Badge variant={item.action === 'skip' ? 'secondary' : 'default'}>
                    {item.action}
                  </Badge>
                )}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveItem(item.file.name)}
                  disabled={isImporting}
                >
                  <AppIcon path={mdiClose} size="sm" />
                </Button>
              </div>
            ))}

            {importQueue.length === 0 && (
              <EmptyState variant="inline" title={t('import.no_files')} />
            )}

            {isImporting && (
              <div className="space-y-2">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-sm text-center text-muted-foreground">
                  Importing... {Math.round(progress)}%
                </p>
              </div>
            )}

            <div className="flex justify-between items-center pt-4">
              <Button
                variant="outline"
                onClick={handleClearAll}
                disabled={isImporting}
              >
                Clear All
              </Button>

              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  disabled={isImporting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={isImporting || importQueue.filter(item => item.status === 'pending' && item.action !== 'skip').length === 0}
                  data-testid="confirm-import"
                >
                  Import {importQueue.filter(item => item.status === 'pending' && item.action !== 'skip').length} Properties
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}